import { queryOptions, UseMutateAsyncFunction, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { OrganizationsAnalyticsService } from "@analytics/index";

import {
  archiveOrganizationUser,
  assignUsersToTechnology,
  listTechnologies,
  listUsers,
  updateOrganization,
} from "@api/organization.api";

import {
  AddAssigneesToTechnologyResponseType,
  OrganizationUsersType,
  UpdateOrganizationRequestType,
  UpdateOrganizationResponseType,
} from "@interfaces/organization.type";

import { PopupActions } from "@store/popupStore";

// Create typed version of the queryClient methods
declare module "@tanstack/react-query" {
  interface Register {
    organizationUsers: {
      data: OrganizationUsersType;
      queryKey: ["organization-users", string];
    };
  }
}

function organizationUsersOptions(organizationId: string) {
  return queryOptions({
    queryFn: async () => {
      return await listUsers(organizationId);
    },
    queryKey: ["organization-users", organizationId], // Include organizationId in the query key for caching
    staleTime: 0, // Adjust staleTime as needed
  });
}

// export const useOrganizationQuery = (organizationId: string) => {
//   return useQuery<any, Error>({
//     queryFn: async () => {
//       return await getOrganization(organizationId);
//     },
//     queryKey: ["organization", organizationId],
//     staleTime: 0,
//   });
// };

export const useOrganizationTechnologiesQuery = (organizationId?: string) => {
  // TODO: Review enabled prop and organizationId! usage. This is to allow conditional use of hooks in PortfolioTable. There's a better solution out there
  return useQuery({
    enabled: !!organizationId, // Only run when organizationId exists
    queryFn: async () => {
      const response = await listTechnologies(organizationId!); // Use ! since we know it exists when queryFn runs
      return { portfolio: response };
    },
    queryKey: ["portfolio", organizationId],
    staleTime: 0,
  });
};

export const useOrganizationUsersQuery = (organizationId: string) => {
  return useQuery(organizationUsersOptions(organizationId));
};

interface ISetTechnologyAssignees {
  organizationId: string;
  technologyId: string;
  userIds: string[];
}

export const useSetTechnologyAssignees = () => {
  const queryClient = useQueryClient();
  return useMutation<
    AddAssigneesToTechnologyResponseType,
    Error,
    ISetTechnologyAssignees,
    AddAssigneesToTechnologyResponseType
  >({
    mutationFn: async ({ technologyId, userIds }) => {
      return await assignUsersToTechnology(userIds, technologyId);
    },
    onError: () => {
      PopupActions.openNotification({
        content: "Something's wrong. Try again later.",
        title: "Error",
      });
    },
    onSuccess: async (_, { organizationId }) => {
      PopupActions.openAlert({
        duration: 3000,
        message: `Assignee(s) updated.`,
        severity: "success",
      });

      const technologies = await listTechnologies(organizationId);
      queryClient.setQueryData(["portfolio", organizationId], technologies);
      OrganizationsAnalyticsService.technologyAssigned();
    },
  });
};

export const useUpdateOrganization = () => {
  return useMutation<
    UpdateOrganizationResponseType,
    Error,
    { organizationId: string } & UpdateOrganizationRequestType,
    UpdateOrganizationResponseType
  >({
    mutationFn: async ({ organizationId, ...rest }) => {
      return await updateOrganization(organizationId, rest);
    },
    onError: () => {
      PopupActions.openNotification({
        content: "Something's wrong. Try again later.",
        title: "Error",
      });
    },
    onSuccess: () => {
      PopupActions.closeModal();
      PopupActions.openAlert({
        duration: 3000,
        message: `Organization Updated Successfully.`,
        severity: "success",
      });
    },
  });
};

interface IArchiveUserInput {
  firstName: string;
  lastName: string;
  organizationId: string;
  userAccountId: string;
}

export type IArchiveUserMutationType = UseMutateAsyncFunction<void, Error, IArchiveUserInput, OrganizationUsersType>;

export const useArchiveUserQuery = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // return type of mutationFn
    Error, // error type
    IArchiveUserInput, // input variables type
    OrganizationUsersType // context type (what onMutate returns)
  >({
    mutationFn: async ({ userAccountId }) => {
      return await archiveOrganizationUser(userAccountId);
    },
    onError: (_, __, previousData) => {
      if (previousData) {
        queryClient.setQueryData(["organization-users"], previousData);
      }
      PopupActions.openNotification({
        content: "Something's wrong. Try again later.",
        title: "Error",
      });
    },
    onMutate: async ({ organizationId, userAccountId }) => {
      await queryClient.cancelQueries({
        queryKey: organizationUsersOptions(organizationId).queryKey,
      });
      const previousUsers = queryClient.getQueryData(organizationUsersOptions(organizationId).queryKey);
      queryClient.setQueryData(organizationUsersOptions(organizationId).queryKey, (old) => {
        if (old) {
          return old.filter((user) => user.userAccountId !== userAccountId);
        }
        return [];
      });
      return previousUsers;
    },
    onSuccess: (_, archivedUser) => {
      PopupActions.closeModal();
      PopupActions.openAlert({
        duration: 3000,
        message: `User deleted.`,
        severity: "success",
      });
      OrganizationsAnalyticsService.userRemoved(
        archivedUser.firstName + " " + archivedUser.lastName,
        archivedUser.userAccountId,
      );
    },
  });
};
